/**
 * @fileoverview A student's practice recordings (rehearsals) as the instructor
 * reviews them on the student's page (iOS review B5).
 *
 * A rehearsal describes the student's week rather than one lesson, so it is
 * reviewed beside the student's assignments instead of under a lesson. The
 * list comes from `GET /students/{id}/practice-recordings` (newest first); the
 * recording page picks one out of the same list, because there is no
 * single-recording endpoint. Instructors never get the audio: it is deleted
 * once Hans has listened, so nothing here offers playback.
 */

import { clipLength } from "@/lib/savedRecordings";
import { cheapestAiFeatureTier, tierDisplayName } from "@/lib/tiers";

/** Statuses a recording passes through before Hans has finished with it. */
const IN_FLIGHT = new Set(["awaiting_upload", "pending", "processing"]);

/**
 * Whether the practice-recordings surfaces show, per the SERVER flag on
 * `/auth/me`.
 *
 * Unlike `rehearsalsEnabled`, an ABSENT flag reads as off: these surfaces are
 * new on the student page, so a payload that predates the field has nothing
 * here to keep alive, and the list endpoint 404s whenever rehearsals are off.
 *
 * @param {object|null|undefined} user The instructor from AuthContext.
 * @returns {boolean}
 */
export function practiceRecordingsEnabled(user) {
  return user?.flags?.rehearsals_enabled === true;
}

/**
 * Where a recording stands for the instructor. Mirrors `practiceState` in
 * `mobile/src/utils/practiceRecordings.js`: once Hans has finished (or given
 * up), it waits on the instructor's word until there is some.
 *
 * @param {object} rec A practice recording.
 * @returns {"processing"|"feedback_given"|"needs_feedback"}
 */
export function practiceRecordingState(rec) {
  if (!rec || IN_FLIGHT.has(rec.processing_status)) return "processing";
  if ((rec.instructor_notes || "").trim()) return "feedback_given";
  return "needs_feedback";
}

const toDate = (value) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};

/** "Oct 30 · 2:14": the row's subtitle on the student page. */
export function practiceRecordingMeta(rec) {
  const d = toDate(rec?.created_at);
  return [
    d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null,
    clipLength(rec?.duration_seconds),
  ]
    .filter(Boolean)
    .join(" · ");
}

/** "Practice recording · Thu, Oct 30 · 2:14": the recording page's eyebrow. */
export function practiceRecordingEyebrow(rec) {
  const d = toDate(rec?.created_at);
  return [
    "Practice recording",
    d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : null,
    clipLength(rec?.duration_seconds),
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * The plan the AI-review notice names, or null when nothing on sale includes
 * practice review — the notice then says what is missing and stops there,
 * rather than naming a plan that cannot be bought.
 *
 * @returns {string|null}
 */
export function practiceAiUpgradeTier() {
  const tier = cheapestAiFeatureTier();
  return tier ? tierDisplayName(tier) : null;
}
